/* eslint-disable react-hooks/exhaustive-deps */

'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type CurrencyContextType = {
    currency: string;
    setCurrency: (currency: string) => void;
    exchangeRates: Record<string, number>;
    getExchangeRate: (from: string, to: string) => Promise<number>;
};

const CurrencyContext = createContext<CurrencyContextType>({
    currency: 'USD',
    setCurrency: () => {},
    exchangeRates: {},
    getExchangeRate: async () => 1,
});

export const useCurrency = (): CurrencyContextType => useContext(CurrencyContext);

interface CurrencyProviderProps {
    children: ReactNode;
}

export function CurrencyProvider({ children }: CurrencyProviderProps) {
    const [currency, setCurrency] = useState<string>('USD');
    // On mount, read any stored preference and update state (avoids SSR hydration mismatch)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('currency');
            if (stored && stored !== currency) {
                setCurrency(stored);
            }
        }
        // We intentionally leave `currency` out of the dependency list
        // so this runs only once after initial mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('currency', currency);
        }
    }, [currency]);

    const getExchangeRate = useCallback(
        async (from: string, to: string): Promise<number> => {
            const key = `${from}_${to}`.toUpperCase();
            const reverseKey = `${to}_${from}`.toUpperCase();

            // Check if the direct rate exists
            if (exchangeRates[key]) {
                return exchangeRates[key];
            }
            // Check if the reverse rate exists and return its inverse
            if (exchangeRates[reverseKey]) {
                return 1 / exchangeRates[reverseKey];
            }

            // If not cached, fetch from a free API
            try {
                const res = await fetch(`https://open.er-api.com/v6/latest/${from}`);
                const data = await res.json();

                if (data.result === 'success' && data.rates && data.rates[to]) {
                    const rate = data.rates[to] as number;
                    // Cache the fetched rate
                    setExchangeRates(prev => ({ ...prev, [key]: rate }));
                    return rate;
                }
                throw new Error('Failed to fetch exchange rate.');
            } catch (error) {
                console.error('Error fetching exchange rate:', error);
                throw error;
            }
        },
        [exchangeRates],
    );

    const value = useMemo(
        () => ({
            currency,
            setCurrency,
            exchangeRates,
            getExchangeRate,
        }),

        [currency],
    );

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
